type LeaderboardType = "overall" | "town" | "mafia" | "active" | "survival";

interface LeaderboardEntry {
    rank: number;
    user_id: string | number;
    username: string;
    metric_value: number | null;
    games_played: number;
    wins?: number;
}

interface LeaderboardResponse {
    leaderboard: LeaderboardEntry[];
    filters?: Record<string, unknown>;
}

interface PlayerStats {
    games_played?: number;
    overall_winrate?: number;
    survival_rate?: number;
    average_survival_round?: number;
    town_games?: number;
    town_wins?: number;
    town_winrate?: number;
    mafia_games?: number;
    mafia_wins?: number;
    mafia_winrate?: number;
    death_breakdown?: Record<string, number>;
    role_stats?: Record<string, { games?: number; wins?: number; winrate?: number }>;
}

interface PlayerRecentGame {
    game_id: string;
    finished_at?: string;
    winner?: string;
    player_role?: string;
    player_alignment?: string;
    survived?: boolean;
    won?: boolean;
    total_rounds?: number;
}

interface PlayerProfile {
    user_id: string | number;
    username: string;
    stats?: PlayerStats;
    recent_games?: PlayerRecentGame[];
}

interface LeaderboardOptions {
    apiBaseUrl: string;
    authenticatedFetch: (url: string, options?: RequestInit) => Promise<Response>;
    onOpen: () => void;
    onClose: () => void;
    getCurrentUser: () => { user_id: string; username: string } | null;  // Changed to string for Firestore
    onRequireLogin: () => void;
}

export interface LeaderboardController {
    open: () => void;
    close: () => void;
}

const LEADERBOARD_TYPES: Array<{ value: LeaderboardType; label: string }> = [
    { value: "overall", label: "Overall" },
    { value: "town", label: "Town" },
    { value: "mafia", label: "Mafia" },
    { value: "survival", label: "Survival" },
    { value: "active", label: "Active" },
];

const TIME_FILTERS: Array<{ value: string; label: string }> = [
    { value: "all_time", label: "All Time" },
    { value: "last_30_days", label: "Last 30 Days" },
    { value: "last_7_days", label: "Last 7 Days" },
    { value: "today", label: "Today" },
];

const DEFAULT_FILTERS = {
    type: "overall" as LeaderboardType,
    timeFilter: "all_time",
};

export function initLeaderboardUI(options: LeaderboardOptions): LeaderboardController {
    let container: HTMLDivElement | null = null;
    let tableContainer: HTMLDivElement | null = null;
    let statusElement: HTMLDivElement | null = null;
    let errorElement: HTMLDivElement | null = null;
    let typeSelect: HTMLSelectElement | null = null;
    let timeSelect: HTMLSelectElement | null = null;
    let loadingLabel: HTMLDivElement | null = null;
    let activeModal: HTMLDivElement | null = null;
    let previousOverflow: string | null = null;
    let isLoading = false;

    const controller: LeaderboardController = {
        open: () => {
            if (!options.getCurrentUser()) {
                options.onRequireLogin();
                return;
            }

            if (!container) {
                buildOverlay();
            }

            if (!container) return;

            previousOverflow = document.body.style.overflow;
            document.body.style.overflow = "hidden";
            options.onOpen();
            container.style.display = "flex";
            container.classList.remove("leaderboard-hidden");
            void loadLeaderboard();
        },
        close: () => {
            if (container) {
                container.classList.add("leaderboard-hidden");
                container.style.display = "none";
            }
            if (activeModal) {
                activeModal.remove();
                activeModal = null;
            }
            document.body.style.overflow = previousOverflow ?? "";
            options.onClose();
        },
    };

    function buildOverlay(): void {
        container = document.createElement("div");
        container.id = "leaderboard-view";
        container.style.cssText = [
            "position: fixed",
            "inset: 0",
            "background: linear-gradient(140deg, rgba(10,10,19,0.92), rgba(12,8,22,0.95))",
            "color: #f8fafc",
            "padding: 24px",
            "display: flex",
            "flex-direction: column",
            "gap: 14px",
            "z-index: 2600",
            "overflow-y: auto",
        ].join(";");

        const header = document.createElement("div");
        header.style.cssText = "display: flex; justify-content: space-between; align-items: center; gap: 12px;";
        const title = document.createElement("div");
        title.innerHTML = `<h1 style="margin:0;font-size:26px;">Leaderboard</h1><p style="margin:4px 0 0;color:rgba(248,250,252,0.7);font-size:13px;">Filter top players and inspect detailed stats.</p>`;
        const closeButton = document.createElement("button");
        closeButton.textContent = "Close";
        closeButton.style.cssText = buttonStyle();
        closeButton.onclick = controller.close;
        header.appendChild(title);
        header.appendChild(closeButton);

        const filters = document.createElement("div");
        filters.style.cssText = "display:flex;flex-wrap:wrap;gap:10px;align-items:flex-end;";

        typeSelect = buildSelect(LEADERBOARD_TYPES, DEFAULT_FILTERS.type, "Leaderboard type");
        timeSelect = buildSelect(TIME_FILTERS, DEFAULT_FILTERS.timeFilter, "Time filter");

        const refresh = document.createElement("button");
        refresh.textContent = "Refresh";
        refresh.style.cssText = buttonStyle();
        refresh.onclick = () => void loadLeaderboard();

        [typeSelect, timeSelect].forEach((el) => {
            if (el) {
                el.onchange = () => void loadLeaderboard();
            }
        });

        filters.appendChild(labeled("Type", typeSelect));
        filters.appendChild(labeled("Time", timeSelect));
        filters.appendChild(refresh);

        tableContainer = document.createElement("div");
        tableContainer.id = "leaderboard-table-container";
        tableContainer.style.cssText = [
            "border: 1px solid rgba(255,255,255,0.08)",
            "border-radius: 12px",
            "overflow: hidden",
            "background: rgba(255,255,255,0.03)",
            "min-height: 240px",
        ].join(";");

        const footer = document.createElement("div");
        footer.style.cssText = "display:flex;justify-content:space-between;align-items:center;padding:10px 12px;border-top:1px solid rgba(255,255,255,0.08);background:rgba(255,255,255,0.02);";

        statusElement = document.createElement("div");
        statusElement.style.cssText = "color: rgba(248,250,252,0.75); font-size: 12px;";
        errorElement = document.createElement("div");
        errorElement.style.cssText = "color: #fca5a5; font-size: 12px; display: none;";
        loadingLabel = document.createElement("div");
        loadingLabel.style.cssText = "color: rgba(248,250,252,0.65); font-size: 12px; visibility: hidden;";
        loadingLabel.textContent = "Loading…";

        const footerLeft = document.createElement("div");
        footerLeft.appendChild(statusElement);
        footerLeft.appendChild(errorElement);

        footer.appendChild(footerLeft);
        footer.appendChild(loadingLabel);

        container.appendChild(header);
        container.appendChild(filters);
        container.appendChild(tableContainer);
        container.appendChild(footer);
        document.body.appendChild(container);

        container.addEventListener("click", (event: MouseEvent) => {
            if (event.target === container) {
                controller.close();
            }
        });

        document.addEventListener("keydown", (event: KeyboardEvent) => {
            if (event.key === "Escape") {
                controller.close();
            }
        });

        container.style.display = "none";
    }

    async function loadLeaderboard(): Promise<void> {
        if (!typeSelect || !timeSelect || !tableContainer) return;

        const leaderboardType = (typeSelect.value as LeaderboardType) || DEFAULT_FILTERS.type;
        const timeFilter = timeSelect.value || DEFAULT_FILTERS.timeFilter;

        isLoading = true;
        clearError();
        setLoading(true);
        renderPlaceholder("Fetching leaderboard…");

        try {
            const response = await options.authenticatedFetch(
                `${options.apiBaseUrl}/api/leaderboard/human/${leaderboardType}?time_filter=${encodeURIComponent(timeFilter)}&limit=100`
            );
            if (!response.ok) {
                const text = await safeReadText(response);
                throw new Error(text || `Failed to load leaderboard (${response.status})`);
            }

            const payload = (await response.json()) as LeaderboardResponse;
            const entries = Array.isArray(payload.leaderboard) ? payload.leaderboard : [];
            renderLeaderboard(entries, leaderboardType);
            if (statusElement) {
                statusElement.textContent = `${entries.length} players | ${timeFilter.split("_").join(" ")}`;
            }
        } catch (error) {
            const message = error instanceof Error ? error.message : "Unknown error";
            showError(message);
            renderPlaceholder("Unable to load leaderboard.");
        } finally {
            isLoading = false;
            setLoading(false);
        }
    }

    function renderLeaderboard(entries: LeaderboardEntry[], leaderboardType: LeaderboardType): void {
        if (!tableContainer) return;
        tableContainer.innerHTML = "";

        if (entries.length === 0) {
            renderPlaceholder("No players match these filters yet.");
            return;
        }

        const table = document.createElement("table");
        table.style.cssText = "width:100%;border-collapse:collapse;";

        const thead = document.createElement("thead");
        thead.style.cssText = "background: rgba(255,255,255,0.06);";
        const headerRow = document.createElement("tr");
        ["Rank", "Player", metricHeading(leaderboardType), "Games", "Wins"].forEach((label) => {
            const th = document.createElement("th");
            th.textContent = label;
            th.style.cssText = "text-align:left;padding:12px;color:rgba(248,250,252,0.85);font-size:13px;border-bottom:1px solid rgba(255,255,255,0.08);";
            headerRow.appendChild(th);
        });
        thead.appendChild(headerRow);

        const tbody = document.createElement("tbody");
        entries.forEach((entry, index) => {
            const row = document.createElement("tr");
            row.style.cssText = `${index % 2 === 0 ? "background: rgba(255,255,255,0.02);" : ""}cursor:pointer;`;
            row.onmouseenter = () => (row.style.background = "rgba(255,255,255,0.05)");
            row.onmouseleave = () => (row.style.background = index % 2 === 0 ? "rgba(255,255,255,0.02)" : "");
            row.onclick = () => void showPlayerProfile(entry.user_id);

            const metricValue = normalizeNumber(entry.metric_value);
            const gamesPlayed = normalizeNumber(entry.games_played);
            const wins = normalizeNumber(entry.wins);

            const cells: Array<string> = [
                entry.rank.toString(),
                entry.username,
                formatMetric(metricValue, leaderboardType),
                gamesPlayed.toString(),
                wins.toString(),
            ];

            cells.forEach((value, cellIndex) => {
                const td = document.createElement("td");
                td.textContent = value;
                td.style.cssText = [
                    "padding: 10px 12px",
                    "font-size: 14px",
                    cellIndex === 2 ? "color: #7ee0ff; font-weight:700;" : "color:#e5e7eb;",
                ].join(" ");
                row.appendChild(td);
            });

            tbody.appendChild(row);
        });

        table.appendChild(thead);
        table.appendChild(tbody);
        tableContainer.appendChild(table);
    }

    async function showPlayerProfile(userId: string | number): Promise<void> {
        if (isLoading) return;
        isLoading = true;
        setLoading(true);
        clearError();

        try {
            const response = await options.authenticatedFetch(`${options.apiBaseUrl}/api/player/${userId}`);
            if (!response.ok) {
                const text = await safeReadText(response);
                throw new Error(text || `Failed to load player (${response.status})`);
            }

            const profile = (await response.json()) as PlayerProfile;
            if (activeModal) {
                activeModal.remove();
            }
            activeModal = buildPlayerModal(profile);
            document.body.appendChild(activeModal);
        } catch (error) {
            const message = error instanceof Error ? error.message : "Could not load player details";
            showError(message);
        } finally {
            isLoading = false;
            setLoading(false);
        }
    }

    function buildPlayerModal(profile: PlayerProfile): HTMLDivElement {
        const overlay = document.createElement("div");
        overlay.style.cssText = "position:fixed;inset:0;background:rgba(0,0,0,0.6);display:flex;align-items:center;justify-content:center;z-index:3000;padding:16px;";
        const modal = document.createElement("div");
        modal.style.cssText = "width:100%;max-width:860px;max-height:90vh;overflow-y:auto;background:linear-gradient(145deg, rgba(26,26,53,0.95), rgba(19,18,40,0.94));border:1px solid rgba(255,255,255,0.08);border-radius:12px;padding:18px;box-shadow:0 16px 48px rgba(0,0,0,0.55);";

        const header = document.createElement("div");
        header.style.cssText = "display:flex;justify-content:space-between;align-items:center;gap:12px;";
        const title = document.createElement("h2");
        title.textContent = `${profile.username}'s Stats`;
        title.style.margin = "0";
        const closeButton = document.createElement("button");
        closeButton.textContent = "Close";
        closeButton.style.cssText = buttonStyle();
        closeButton.onclick = () => overlay.remove();
        header.appendChild(title);
        header.appendChild(closeButton);

        const stats = profile.stats ?? {};

        const grid = document.createElement("div");
        grid.style.cssText = "display:grid;grid-template-columns:repeat(auto-fit,minmax(200px,1fr));gap:10px;margin-top:12px;";
        grid.appendChild(statCard("Total Games", stats.games_played));
        grid.appendChild(statCard("Overall Win Rate", formatPercent(stats.overall_winrate)));
        grid.appendChild(statCard("Survival Rate", formatPercent(stats.survival_rate)));
        grid.appendChild(statCard("Avg Survival Round", formatNumber(stats.average_survival_round, 1)));
        grid.appendChild(statCard("Town Record", formatRecord(stats.town_wins, stats.town_games)));
        grid.appendChild(statCard("Mafia Record", formatRecord(stats.mafia_wins, stats.mafia_games)));

        const deathSection = document.createElement("div");
        deathSection.style.cssText = "margin-top:14px;";
        const deathTitle = document.createElement("h3");
        deathTitle.textContent = "Elimination Stats";
        deathTitle.style.margin = "0 0 6px";
        deathSection.appendChild(deathTitle);
        const deathBreakdown = stats.death_breakdown ?? {};
        if (Object.keys(deathBreakdown).length === 0) {
            const empty = document.createElement("p");
            empty.textContent = "No elimination data yet.";
            empty.style.cssText = "color: rgba(248,250,252,0.75); margin:0;";
            deathSection.appendChild(empty);
        } else {
            const list = document.createElement("div");
            list.style.cssText = "display:grid;grid-template-columns:repeat(auto-fit,minmax(160px,1fr));gap:8px;";
            Object.entries(deathBreakdown).forEach(([label, amount]) => {
                const item = document.createElement("div");
                item.textContent = `${prettify(label)}: ${amount ?? 0}`;
                item.style.cssText = "padding:8px;border:1px solid rgba(255,255,255,0.08);border-radius:10px;background:rgba(255,255,255,0.02);";
                list.appendChild(item);
            });
            deathSection.appendChild(list);
        }

        const recentSection = document.createElement("div");
        recentSection.style.cssText = "margin-top:14px;";
        const recentTitle = document.createElement("h3");
        recentTitle.textContent = "Recent Games";
        recentTitle.style.margin = "0 0 6px";
        recentSection.appendChild(recentTitle);
        const recentGames = profile.recent_games ?? [];
        if (recentGames.length === 0) {
            const empty = document.createElement("p");
            empty.textContent = "No recent games recorded.";
            empty.style.cssText = "color: rgba(248,250,252,0.75); margin:0;";
            recentSection.appendChild(empty);
        } else {
            const list = document.createElement("div");
            list.style.cssText = "display:flex;flex-direction:column;gap:8px;";
            recentGames.forEach((game) => {
                const row = document.createElement("div");
                row.style.cssText = "padding:10px;border:1px solid rgba(255,255,255,0.08);border-radius:10px;background:rgba(255,255,255,0.02);display:grid;grid-template-columns:repeat(auto-fit,minmax(160px,1fr));gap:6px;";
                row.appendChild(simpleRow("Result", resultLabel(game)));
                row.appendChild(simpleRow("Role", prettify(game.player_role ?? "")));
                row.appendChild(simpleRow("Rounds", formatNumber(game.total_rounds)));
                row.appendChild(simpleRow("Finished", formatTimestamp(game.finished_at)));
                list.appendChild(row);
            });
            recentSection.appendChild(list);
        }

        modal.appendChild(header);
        modal.appendChild(grid);
        modal.appendChild(deathSection);
        modal.appendChild(recentSection);
        overlay.appendChild(modal);
        overlay.onclick = (event: MouseEvent) => {
            if (event.target === overlay) {
                overlay.remove();
            }
        };
        return overlay;
    }

    function statCard(label: string, value: string | number | undefined | null): HTMLDivElement {
        const card = document.createElement("div");
        card.style.cssText = "padding:10px;border:1px solid rgba(255,255,255,0.08);border-radius:10px;background:rgba(255,255,255,0.02);";
        const title = document.createElement("div");
        title.textContent = label;
        title.style.cssText = "font-size:12px;color:rgba(248,250,252,0.7);";
        const body = document.createElement("div");
        body.textContent = typeof value === "number" ? value.toString() : value ?? "—";
        body.style.cssText = "font-size:18px;font-weight:700;";
        card.appendChild(title);
        card.appendChild(body);
        return card;
    }

    function simpleRow(label: string, value: string): HTMLDivElement {
        const row = document.createElement("div");
        row.style.cssText = "color: rgba(248,250,252,0.82); font-size: 13px;";
        row.textContent = `${label}: ${value}`;
        return row;
    }

    function renderPlaceholder(message: string): void {
        if (!tableContainer) return;
        tableContainer.innerHTML = "";
        const placeholder = document.createElement("div");
        placeholder.textContent = message;
        placeholder.style.cssText = "padding:20px;text-align:center;color:rgba(248,250,252,0.75);";
        tableContainer.appendChild(placeholder);
    }

    function setLoading(value: boolean): void {
        if (loadingLabel) {
            loadingLabel.style.visibility = value ? "visible" : "hidden";
        }
        [typeSelect, timeSelect].forEach((control) => {
            if (control) control.disabled = value;
        });
    }

    function showError(message: string): void {
        if (!errorElement) return;
        errorElement.textContent = message;
        errorElement.style.display = "block";
    }

    function clearError(): void {
        if (!errorElement) return;
        errorElement.textContent = "";
        errorElement.style.display = "none";
    }

    return controller;
}

function metricHeading(type: LeaderboardType): string {
    if (type === "active") return "Games Played";
    if (type === "survival") return "Survival Rate";
    return "Win Rate";
}

function formatMetric(value: number, type: LeaderboardType): string {
    if (!Number.isFinite(value)) return type === "active" ? "0" : "0%";
    if (type === "active") return value.toFixed(0);
    return `${(value * 100).toFixed(1)}%`;
}

function normalizeNumber(value: unknown): number {
    const numeric = typeof value === "string" ? Number.parseFloat(value) : (value as number);
    if (Number.isFinite(numeric)) return numeric;
    return 0;
}

function formatPercent(value: number | undefined | null): string {
    if (value === undefined || value === null || !Number.isFinite(value)) return "—";
    return `${(value * 100).toFixed(1)}%`;
}

function formatNumber(value: number | undefined | null, decimals: number = 0): string {
    if (value === undefined || value === null || !Number.isFinite(value)) return "—";
    return value.toFixed(decimals);
}

function formatRecord(wins: number | undefined, games: number | undefined): string {
    const safeWins = normalizeNumber(wins);
    const safeGames = normalizeNumber(games);
    if (safeGames === 0) return "0 / 0";
    return `${safeWins} / ${safeGames} | ${formatPercent(safeWins / safeGames)}`;
}

function resultLabel(game: PlayerRecentGame): string {
    const win = game.won === true || game.winner?.toLowerCase() === "town" ? "Win" : "Loss";
    const survived = game.survived ? " | Survived" : "";
    return `${win}${survived}`;
}

function formatTimestamp(timestamp?: string): string {
    if (!timestamp) return "—";
    const date = new Date(timestamp);
    if (Number.isNaN(date.getTime())) return timestamp;
    return date.toLocaleString();
}

async function safeReadText(response: Response): Promise<string> {
    try {
        return await response.text();
    } catch {
        return "";
    }
}

function prettify(label: string): string {
    if (!label) return "Unknown";
    return label
        .replace(/_/g, " ")
        .split(" ")
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(" ");
}

function buttonStyle(): string {
    return [
        "background: linear-gradient(90deg, #00d4ff, #7c3aed)",
        "color: #0b0b17",
        "border: none",
        "border-radius: 10px",
        "padding: 10px 14px",
        "font-weight: 700",
        "cursor: pointer",
        "box-shadow: 0 8px 24px rgba(0,212,255,0.25)",
    ].join(";");
}

function inputStyle(width: string): string {
    return [
        `width: ${width}`,
        "padding: 10px",
        "border-radius: 10px",
        "border: 1px solid rgba(255,255,255,0.08)",
        "background: rgba(255,255,255,0.04)",
        "color: #e8f4ff",
        "font-weight: 600",
        "outline: none",
    ].join(";");
}

function buildSelect(options: Array<{ value: string; label: string }>, defaultValue: string, ariaLabel: string): HTMLSelectElement {
    const select = document.createElement("select");
    select.ariaLabel = ariaLabel;
    select.style.cssText = inputStyle("140px");

    options.forEach((opt) => {
        const option = document.createElement("option");
        option.value = opt.value;
        option.text = opt.label;
        select.appendChild(option);
    });

    select.value = defaultValue;
    return select;
}

function labeled(label: string, control: HTMLElement): HTMLDivElement {
    const wrapper = document.createElement("div");
    wrapper.style.cssText = "display:flex;flex-direction:column;gap:6px;";
    const caption = document.createElement("label");
    caption.textContent = label;
    caption.style.cssText = "font-size:12px;color:rgba(248,250,252,0.7);";
    wrapper.appendChild(caption);
    wrapper.appendChild(control);
    return wrapper;
}
